import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Code, Palette, Briefcase, TrendingUp, HardHat, ArrowRight } from "lucide-react";
import { endpoints } from "@/lib/api";
import { usePersona } from "@/lib/personaStore";
import { useBranding } from "@/lib/branding";
import { Button } from "@/components/ui/button";

const ICONS = { Code, Palette, Briefcase, TrendingUp, HardHat };
// All approval requests, decision notifications and transactional emails from
// a user-mode session go here by default. Kept inline (not in UI) so the
// persona picker stays as a one-click gate.
const DEFAULT_SESSION_EMAIL = "manchit.kalani@gmail.com";

export default function PersonaPicker() {
  const [personas, setPersonas] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();
  const { setPersona, setUserName, setUserEmail } = usePersona();
  const { brand } = useBranding();

  useEffect(() => {
    endpoints.personas().then((d) => { setPersonas(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const canEnter = !!selectedId;

  const enter = () => {
    if (!canEnter) return;
    const p = personas.find((x) => x.id === selectedId);
    if (!p) return;
    setPersona(p);
    setUserEmail(DEFAULT_SESSION_EMAIL);
    nav("/home");
  };

  const logoShape =
    brand.logoShape === "chevron"
      ? { clipPath: "polygon(0 0, 100% 0, 100% 60%, 50% 100%, 0 60%)" }
      : { borderRadius: 8 };

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="persona-picker-page">
      <div className="stripe-bar h-1.5 w-full" />
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 grid place-items-center text-white font-black text-lg"
              style={{ ...logoShape, backgroundColor: brand.primary }}
            >
              {brand.logoLetter}
            </div>
            <div>
              <div className="font-display font-bold text-lg text-slate-900">{brand.wordmark}</div>
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold -mt-1">{brand.small}</div>
            </div>
          </div>
          <div className="text-xs text-slate-500 hidden sm:block">Internal Employee Portal · v1.0</div>
        </div>
      </header>

      <main className="flex-1 corp-grid">
        <div className="max-w-7xl mx-auto px-8 py-14">
          <div className="grid lg:grid-cols-[1.1fr_1fr] gap-12 items-start">
            <div>
              <div
                className="inline-flex items-center gap-2 px-3 py-1 rounded-md text-xs font-semibold tracking-wide"
                style={{ backgroundColor: `${brand.primary}1a`, color: brand.primary }}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-[#DA291C]" />
                DEVICE SELF-SERVICE
              </div>
              <h1 className="mt-4 font-display font-black text-5xl lg:text-6xl leading-[1.05] text-slate-900 tracking-tight">
                {brand.heroTitle}<br />
                <span style={{ color: brand.primary }}>{brand.heroAccent}</span>
              </h1>
              <p className="mt-5 text-slate-600 max-w-xl text-base leading-relaxed">
                {brand.heroBody}
              </p>

              <div className="mt-8 grid sm:grid-cols-2 gap-4 max-w-lg">
                <div className="border border-slate-200 bg-white rounded-md p-4">
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Personas</div>
                  <div className="mt-1 font-display font-bold text-3xl text-slate-900">5</div>
                  <div className="text-xs text-slate-500">Role-based catalogues</div>
                </div>
                <div className="border border-slate-200 bg-white rounded-md p-4">
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Ticketing</div>
                  <div className="mt-1 font-display font-bold text-3xl text-slate-900">Auto</div>
                  <div className="text-xs text-slate-500">ServiceNow REQ generated</div>
                </div>
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-md p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <h2 className="font-display font-bold text-xl text-slate-900">Select your persona</h2>
                <span className="text-xs text-slate-500">Demo mode</span>
              </div>
              <p className="text-sm text-slate-500 mt-1">Choose the role that best matches your day-to-day.</p>

              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="persona-list">
                {loading && Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="h-24 rounded-md bg-slate-100 animate-pulse" />
                ))}
                {personas.map((p) => {
                  const Icon = ICONS[p.icon] || Briefcase;
                  const active = selectedId === p.id;
                  return (
                    <button
                      key={p.id}
                      data-testid={`persona-card-${p.id}`}
                      onClick={() => setSelectedId(p.id)}
                      className={`persona-card text-left border rounded-md p-4 bg-white flex items-start gap-3 ${active ? "" : "border-slate-200"}`}
                      style={active ? { borderColor: brand.primary, boxShadow: `0 0 0 3px ${brand.primary}22` } : {}}
                    >
                      <div
                        className="w-10 h-10 shrink-0 grid place-items-center rounded-md text-white"
                        style={{ backgroundColor: p.id === "field_engineer" ? "#DA291C" : brand.primary }}
                      >
                        <Icon size={20} />
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold text-slate-900 text-sm">{p.name}</div>
                        <div className="text-xs text-slate-500">{p.title}</div>
                        <div className="text-[11px] text-slate-500 mt-1 line-clamp-2">{p.description}</div>
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="mt-5 grid gap-3">
                <div className="text-[11px] text-slate-500 leading-relaxed bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
                  <span className="font-semibold text-slate-700">Note:</span> Approval requests and notification emails from this session route to <span className="font-mono text-slate-700">{DEFAULT_SESSION_EMAIL}</span>.
                </div>
                <Button
                  data-testid="enter-flexspace-btn"
                  disabled={!canEnter}
                  onClick={enter}
                  className="w-full text-white h-11 font-semibold rounded-md group"
                  style={{ backgroundColor: brand.primary }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = brand.primaryDark; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = brand.primary; }}
                >
                  {brand.ctaLabel}
                  <ArrowRight size={18} className="ml-2 transition-transform group-hover:translate-x-0.5" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>

      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-8 py-4 text-xs text-slate-500 flex flex-wrap justify-between gap-2">
          <span>{brand.footer}</span>
          <span>Powered by ServiceNow ITSM · Flexspace v1.0</span>
        </div>
      </footer>
    </div>
  );
}
